// src/pages/Login.js
import React, { useState } from "react";
import { auth } from "../lib/firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Layout from "../central/Layout";
import Panel from "../central/Panel";
import Button from "../central/Button";

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: ${({ theme }) => theme.spacing.xxlarge};
  min-height: calc(100vh - 80px);
  background-color: ${({ theme }) => theme.colors.background};
`;

const FormPanel = styled(Panel)`
  width: 100%;
  max-width: 420px;
  padding: ${({ theme }) => theme.spacing.xlarge};
`;

const Title = styled.h2`
  text-align: center;
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  margin-bottom: ${({ theme }) => theme.spacing.large};
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.error};
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.medium};
`;

const Input = styled.input`
  padding: ${({ theme }) => theme.spacing.small};
  border-radius: ${({ theme }) => theme.radii.small};
  border: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-family: ${({ theme }) => theme.typography.fontFamily.body};
  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  transition: border-color ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}33;
  }
`;

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate("/home");
    } catch (err) {
      console.error("Login error:", err.message);
      setError(err.message);
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setError("Enter your email to reset password.");
      return;
    }
    try {
      const functions = getFunctions(undefined, "us-central1");
      const sendCustomPasswordResetEmail = httpsCallable(
        functions,
        "sendCustomPasswordResetEmail"
      );
      await sendCustomPasswordResetEmail({ email });
      setResetMessage("Check your email for a reset link.");
      setError("");
    } catch (err) {
      console.error("Custom password reset email error:", err.message);
      setError(err.message);
      setResetMessage("");
    }
  };

  return (
    <Layout>
      <Container>
        <FormPanel>
          <Title>Sign In</Title>
          {error && <ErrorText>{error}</ErrorText>}
          {resetMessage && (
            <p style={{ color: "#2e7d32", textAlign: "center", marginBottom: "1rem" }}>
              {resetMessage}
            </p>
          )}
          <StyledForm onSubmit={handleLogin}>
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <div style={{ textAlign: "right", marginTop: "-8px" }}>
              <button
                type="button"
                onClick={handleResetPassword}
                style={{
                  background: "none",
                  border: "none",
                  color: "#666",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  textDecoration: "underline",
                }}
              >
                Forgot password?
              </button>
            </div>
            <Button 
              type="submit"
              variant="pink"
              >Sign In</Button>
            <div style={{ textAlign: "center", marginTop: "1rem" }}>
              <p style={{ color: "#333", marginBottom: "0.5rem" }}>Don't have an account?</p>
              <Button
                type="button"
                onClick={() => navigate("/signup")}
                variant="blue"
                style={{ width: "100%" }}
              >
                Sign Up
              </Button>
            </div>
          </StyledForm>
        </FormPanel>
      </Container>
    </Layout>
  );
};

export default Login;